/* eslint-disable import/no-cycle */
import { computed, ComputedRef, onUnmounted, watch } from 'vue';
import { useStore } from '@/store';

/**
 * Discreet polling of /billing/overview while the plan is in a "pending"
 * Shopify state (cf. D7 of the refonte plan). Every POLL_INTERVAL_MS the
 * overview is refetched; as soon as plan.status leaves the pending set,
 * the polling stops. Hard stop after MAX_POLL_DURATION_MS so we don't
 * hammer the backend if the shop forgets to approve on Shopify.
 *
 * Used by SubscriptionPage.vue.
 */
const POLL_INTERVAL_MS = 5000;
const MAX_POLL_DURATION_MS = 5 * 60 * 1000;
const PENDING_STATUSES = new Set([
  'pending_payment',
  'pending_capped_increase',
]);

export function usePendingPaymentPolling() {
  const store = useStore();
  let timer: ReturnType<typeof setInterval> | null = null;
  let stopAt = 0;

  const status: ComputedRef<string | null> = computed(
    () => store.state.billing.overview?.plan?.status ?? null,
  );

  const stop = () => {
    if (timer !== null) {
      clearInterval(timer);
      timer = null;
    }
  };

  const start = () => {
    stop();
    stopAt = Date.now() + MAX_POLL_DURATION_MS;
    timer = setInterval(async () => {
      if (Date.now() > stopAt) {
        stop();
        return;
      }
      const current = status.value;
      if (!current || !PENDING_STATUSES.has(current)) {
        stop();
        return;
      }
      await store.dispatch('billing/refetchOverview');
    }, POLL_INTERVAL_MS);
  };

  watch(status, (v) => {
    if (v && PENDING_STATUSES.has(v)) {
      if (timer === null) start();
    } else {
      stop();
    }
  }, { immediate: true });

  onUnmounted(() => stop());

  return { stop };
}
